import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Pagination, Row } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import {
  ConfirmDeleteDialog,
  InfoDialog,
  Loader,
  ScrollToTop,
  SearchBar,
} from "../../../components";
import { ShowTableMerk } from "../../../components/ShowTable";

const TampilMerk = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [merks, setMerks] = useState([]);
  const [selectedMerk, setSelectedMerk] = useState(null);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        dispatch({ type: "LOGOUT" });
        navigate("/");
      }
    },
    [dispatch, navigate]
  );

  const getMerks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/merksPagination?search_query=${encodeURIComponent(
          searchTerm
        )}&page=${page}&limit=20`,
        {
          _id: user.id,
          token: user.token,
        }
      );
      setMerks(response.data.merks);
      setTotalPage(response.data.totalPage);
      setTotalRows(response.data.totalRows);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data Merk.");
    }
    setLoading(false);
  }, [handleAuthError, page, searchTerm, user.id, user.token]);

  const getMerkById = useCallback(async () => {
    if (!selectedId) {
      setSelectedMerk(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/merks/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      setSelectedMerk(response.data);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil detail Merk.");
    }
    setLoading(false);
  }, [handleAuthError, selectedId, user.id, user.token]);

  useEffect(() => {
    getMerks();
  }, [getMerks]);

  useEffect(() => {
    getMerkById();
  }, [getMerkById]);

  const searchData = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  const deleteMerk = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/deleteMerk/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      navigate("/merk");
      setSelectedMerk(null);
      await getMerks();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus Merk.");
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Master</div>
          <h1 className="page-title">Merk</h1>
        </div>
        <Button
          className="btn-icon"
          size="sm"
          onClick={() => navigate("/merk/tambahMerk")}
        >
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedMerk && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button
                className="btn-back"
                size="sm"
                variant="outline-secondary"
                onClick={() => navigate("/merk")}
              >
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail Merk</h2>
                <div className="text-muted">{selectedMerk.nama}</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-primary"
                onClick={() =>
                  navigate(`/merk/${selectedMerk.id}/edit`)
                }
              >
                <MdEdit /> Ubah
              </Button>
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <MdDelete /> Hapus
              </Button>
            </div>
          </div>
          <hr />
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Kode</div>
                <div className="detail-value">
                  {selectedMerk.kode || "-"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Nama</div>
                <div className="detail-value">
                  {selectedMerk.nama || "-"}
                </div>
              </div>
            </Col>
          </Row>
        </section>
      )}

      <Form onSubmit={searchData}>
        <div className="toolbar">
          <div className="d-flex gap-2 flex-wrap">
            <SearchBar setSearchTerm={setQuery} value={query} />
            <Button className="btn-icon" type="submit">
              Cari
            </Button>
          </div>
          <div className="text-muted small">Total data: {totalRows}</div>
        </div>
      </Form>

      <ShowTableMerk
        merks={merks}
        selectedId={selectedId}
        onSelect={(id) => navigate(`/merk/${id}`)}
      />

      {totalPage > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          />
          {Array.from({ length: totalPage }, (_, index) => (
            <Pagination.Item
              key={index}
              active={index === page}
              onClick={() => setPage(index)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page + 1 >= totalPage}
            onClick={() => setPage(page + 1)}
          />
        </Pagination>
      )}

      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus Merk ${selectedMerk?.nama}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={deleteMerk}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default TampilMerk;
